import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../services/api";
import "./DynamicPage.css";

import AnalogCard from "../components/dynamic/AnalogCard";
import DigitalCard from "../components/dynamic/DigitalCard";
import IOCard from "../components/dynamic/IOCard";

const isMobileViewport = () => {
  const hasTouch = navigator.maxTouchPoints > 0
    || window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  return hasTouch && Math.min(window.innerWidth, window.innerHeight) <= 760;
};

const DynamicPage = ({ theme, access }) => {
  const params = useParams();
  const pageKey = params.slug ?? params.id ?? params.pageId ?? params.pk ?? Object.values(params)[0];

  const [page, setPage] = useState(null);
  const [latestMap, setLatestMap] = useState({});
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [columns, setColumns] = useState(() => {
    const saved = Number(localStorage.getItem("ioPageMobileColumns"));
    return saved >= 1 && saved <= 4 ? saved : 1;
  });
  const containerRef = useRef(null);
  const pinchRef = useRef(null);
  const columnsRef = useRef(columns);
  const canWrite = !access || ["superadmin", "admin", "engineer", "operator"].includes(access.role);

  const isNumeric = (v) => /^\d+$/.test(String(v));

  const fetchPage = async () => {
    setError("");
    try {
      if (!pageKey) {
        setError("Route param missing. Check App.js route.");
        return;
      }

      // ✅ ID
      if (isNumeric(pageKey)) {
        const res = await API.get(`/pages/${pageKey}/detail/`);
        setPage(res.data);
        return;
      }

      // ✅ Slug (نیازمند API: /api/pages/slug/<slug>/detail/)
      const res = await API.get(`/pages/slug/${pageKey}/detail/`);
      setPage(res.data);
    } catch (err) {
      console.error("fetchPage error:", err);
      setError("Failed to load page detail.");
    }
  };

  const fetchLatest = async () => {
    try {
      const res = await API.get("/latest-tag-values/");
      const map = {};
      (res.data || []).forEach((x) => {
        map[x.tag_name] = x;
      });
      setLatestMap(map);
    } catch (e) {
      console.error(e);
    }
  };

  // // ✅ Digital write (0/1) -> /api/set-digital-tag/
  // const setDigitalValue = async (tagId, value) => {
  //   await API.post("/set-digital-tag/", { tag_id: tagId, value });
  //   await fetchLatest();
  // };
const setDigitalValue = async (tagId, value) => {
  // ✅ دقیقا مثل IO از همان API استفاده می‌کنیم
  await API.post("/set-tag-value/", { tag_id: tagId, value });
  await fetchLatest();
};
  // ✅ Analog write -> /api/set-tag-value/
  const setAnalogValue = async (tagId, value) => {
    await API.post("/set-tag-value/", { tag_id: tagId, value });
    await fetchLatest();
  };

  useEffect(() => {
    fetchPage();
    fetchLatest();
    const interval = setInterval(fetchLatest, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  useEffect(() => {
    columnsRef.current = columns;
    localStorage.setItem("ioPageMobileColumns", String(columns));
  }, [columns]);

  useEffect(() => {
    const updateLimit = () => setIsMobile(isMobileViewport());
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !isMobile) return undefined;
    const distance = (touches) => Math.hypot(
      touches[1].clientX - touches[0].clientX,
      touches[1].clientY - touches[0].clientY
    );
    const onTouchStart = (event) => {
      if (event.touches.length !== 2) return;
      event.preventDefault();
      pinchRef.current = {distance: distance(event.touches), columns: columnsRef.current};
    };
    const onTouchMove = (event) => {
      if (event.touches.length !== 2 || !pinchRef.current) return;
      event.preventDefault();
      const ratio = pinchRef.current.distance / Math.max(1, distance(event.touches));
      const steps = Math.round(Math.log(ratio) / Math.log(1.28));
      setColumns(Math.min(4, Math.max(1, pinchRef.current.columns + steps)));
    };
    const onTouchEnd = (event) => {
      if (event.touches.length < 2) pinchRef.current = null;
    };
    element.addEventListener("touchstart", onTouchStart, {passive: false});
    element.addEventListener("touchmove", onTouchMove, {passive: false});
    element.addEventListener("touchend", onTouchEnd, {passive: true});
    element.addEventListener("touchcancel", onTouchEnd, {passive: true});
    return () => {
      element.removeEventListener("touchstart", onTouchStart);
      element.removeEventListener("touchmove", onTouchMove);
      element.removeEventListener("touchend", onTouchEnd);
      element.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [page, isMobile]);

  const sections = useMemo(() => page?.sections_detail || [], [page]);

  if (error) {
    return (
      <div className="dg-container" data-theme={theme}>
        <h1>Dynamic Page</h1>
        <div className="dg-error">{error}</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="dg-container" data-theme={theme}>
        <h1>Dynamic Page</h1>
        <div className="dg-muted">Loading...</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="dg-container"
      data-theme={theme}
      data-mobile-layout={isMobile ? "true" : undefined}
      data-columns={isMobile ? columns : undefined}
      style={isMobile ? {"--dg-columns": columns} : undefined}
    >
      <h1 className="dg-title">{page.title}</h1>
      {page.description && <p className="dg-subtitle">{page.description}</p>}

      {isMobile && <div className="dg-zoom-controls" aria-label="I/O page layout zoom">
        <button type="button" onClick={() => setColumns((value) => Math.min(4, value + 1))} disabled={columns === 4} aria-label="Zoom out and show more tags">−</button>
        <span>{columns} {columns === 1 ? "tag" : "tags"} per row</span>
        <button type="button" onClick={() => setColumns((value) => Math.max(1, value - 1))} disabled={columns === 1} aria-label="Zoom in and show fewer tags">+</button>
      </div>}

      {sections.map((slink) => {
        const section = slink.section;
        const mode = section.mode; // analog | digital | io
        const tagLinks = section.tags_detail || [];

        return (
          <div className="dg-section" key={slink.id}>
            <div className="dg-section-head">
              <h2>{section.title}</h2>
              <span className="dg-badge">{mode}</span>
            </div>

            {section.description && <div className="dg-section-desc">{section.description}</div>}

            <div className="dg-grid">
              {tagLinks.map((tagLink) => {
                const tag = tagLink.tag;
                if (mode === "analog") {
                  return <AnalogCard
                    key={tag.id}
                    tag={tag}
                    latestMap={latestMap}
                    title={tagLink.display_title}
                    min={tagLink.min_value ?? 0}
                    max={tagLink.max_value ?? 100}
                    unit={tagLink.unit || ""}
                  />;
                }
                if (mode === "digital") {
                  return (
                    <DigitalCard
                      key={tag.id}
                      tag={tag}
                      latestMap={latestMap}
                      onSetDigital={setDigitalValue}
                      readOnly={!canWrite}
                    />
                  );
                }
                return <IOCard key={tag.id} tag={tag} latestMap={latestMap} onSetAnalog={setAnalogValue} readOnly={!canWrite} />;
              })}

              {tagLinks.length === 0 && <div className="dg-muted">No tags in this section.</div>}
            </div>
          </div>
        );
      })}

      {sections.length === 0 && <div className="dg-muted">This page has no sections yet.</div>}
    </div>
  );
};

export default DynamicPage;
